import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';
import { Globe, ChevronDown, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'es', label: 'Español', flag: 'ES' },
];

const CURRENCY_LIST = [
  { code: 'USD', flag: '🇺🇸' },
  { code: 'EUR', flag: '🇪🇺' },
  { code: 'GBP', flag: '🇬🇧' },
  { code: 'CAD', flag: '🇨🇦' },
  { code: 'MXN', flag: '🇲🇽' },
  { code: 'COP', flag: '🇨🇴' },
  { code: 'BRL', flag: '🇧🇷' },
  { code: 'CHF', flag: '🇨🇭' },
  { code: 'CNY', flag: '🇨🇳' },
  { code: 'JPY', flag: '🇯🇵' },
];

export default function SettingsSelector({ collapsed }) {
  const { i18n, t } = useTranslation();
  const { currencyCode, setCurrency, currencies } = useCurrency();
  const [open, setOpen] = useState(false);

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('blackorex_lang', code);
  };

  if (collapsed) {
    return (
      <button onClick={() => setOpen(!open)}
        className="w-full flex justify-center text-slate-500 hover:text-white transition-colors p-1.5 relative"
        data-testid="settings-toggle">
        <Globe className="w-4 h-4" />
        {open && (
          <div className="absolute left-full bottom-0 ml-2 w-48 bg-[#11141D] border border-white/10 shadow-xl z-50 p-2"
            onClick={e => e.stopPropagation()}>
            <MiniPanel changeLang={changeLang} i18n={i18n} currencyCode={currencyCode}
              setCurrency={setCurrency} currencies={currencies} t={t} />
          </div>
        )}
      </button>
    );
  }

  return (
    <div className="px-3 py-2 border-t border-white/[0.08]" data-testid="settings-panel">
      <button onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-slate-400 hover:text-white transition-colors py-1"
        data-testid="settings-toggle">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4" />
          <span className="text-xs font-medium">{i18n.language.toUpperCase()} / {currencyCode}</span>
        </div>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          <MiniPanel changeLang={changeLang} i18n={i18n} currencyCode={currencyCode}
            setCurrency={setCurrency} currencies={currencies} t={t} />
        </div>
      )}
    </div>
  );
}

function MiniPanel({ changeLang, i18n, currencyCode, setCurrency, currencies, t }) {
  return (
    <>
      <div>
        <p className="text-[10px] uppercase tracking-[0.15em] text-slate-600 mb-1.5">{t('common.language')}</p>
        <div className="flex gap-1">
          {LANGUAGES.map(l => (
            <button key={l.code} onClick={() => changeLang(l.code)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-mono font-bold tracking-wider transition-all ${
                i18n.language === l.code
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-white/5 text-slate-500 border border-white/10 hover:text-white'
              }`}
              data-testid={`lang-${l.code}`}>
              {i18n.language === l.code && <Check className="w-2.5 h-2.5" />}
              {l.flag}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.15em] text-slate-600 mb-1.5">{t('common.currency')}</p>
        <div className="grid grid-cols-5 gap-1">
          {CURRENCY_LIST.map(c => (
            <button key={c.code} onClick={() => setCurrency(c.code)}
              className={`px-1.5 py-1 text-[9px] font-mono font-bold tracking-wider transition-all ${
                currencyCode === c.code
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-white/5 text-slate-500 border border-white/10 hover:text-white'
              }`}
              data-testid={`currency-${c.code}`}
              title={currencies[c.code]?.code}>
              {c.code}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
